// frontend/src/components/layout/Navbar.tsx

import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Zap, User, LogOut, Server } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { Button } from '@/components/ui/Button';
import { LanguageSwitcher } from './LanguageSwitcher';

export function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-40 w-full bg-white bg-opacity-90 backdrop-blur-md border-b border-apple-gray-200">
      <div className="container-main">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-xl font-semibold">
            <Zap className="w-6 h-6 text-apple-blue-500" />
            <span>{t('app_name')}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/orders" className="text-apple-gray-700 hover:text-apple-blue-500 transition">
              {t('nav.orders')}
            </Link>
            <Link to="/trade" className="text-apple-gray-700 hover:text-apple-blue-500 transition">
              {t('nav.trade')}
            </Link>
            <Link to="/guide" className="text-apple-gray-700 hover:text-apple-blue-500 transition">
              {t('guide.nav', 'Guide')}
            </Link>

            <LanguageSwitcher />

            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="text-apple-gray-700 hover:text-apple-blue-500 transition">
                  {t('nav.dashboard')}
                </Link>

                {user?.username === 'admin' && (
                  <Link to="/admin" className="text-apple-gray-700 hover:text-apple-blue-500 transition flex items-center gap-1">
                    <Server className="w-4 h-4" />
                    Admin Panel
                  </Link>
                )}

                <div className="flex items-center gap-2 text-apple-gray-700">
                  <User className="w-4 h-4" />
                  {user?.username}
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout} icon={<LogOut className="w-4 h-4" />}>
                  {t('nav.logout')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">{t('nav.login')}</Button>
                </Link>
                <Link to="/signup">
                  <Button variant="primary" size="sm">{t('nav.signup')}</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-apple-gray-200 animate-slide-down">
            <div className="flex flex-col gap-4">
              <Link to="/orders" className="text-apple-gray-700 hover:text-apple-blue-500"
                onClick={() => setIsMobileMenuOpen(false)}>
                {t('nav.orders')}
              </Link>
              <Link to="/trade" className="text-apple-gray-700 hover:text-apple-blue-500"
                onClick={() => setIsMobileMenuOpen(false)}>
                {t('nav.trade')}
              </Link>
              <Link to="/guide" className="text-apple-gray-700 hover:text-apple-blue-500"
                onClick={() => setIsMobileMenuOpen(false)}>
                {t('guide.nav', 'Guide')}
              </Link>

              <LanguageSwitcher />

              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="text-apple-gray-700 hover:text-apple-blue-500"
                    onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.dashboard')}
                  </Link>

                  {user?.username === 'admin' && (
                    <Link to="/admin" className="text-apple-gray-700 hover:text-apple-blue-500 flex items-center gap-2"
                      onClick={() => setIsMobileMenuOpen(false)}>
                      <Server className="w-4 h-4" />
                      Admin Panel
                    </Link>
                  )}

                  <Link to="/profile" className="text-apple-gray-700 hover:text-apple-blue-500"
                    onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.profile')}
                  </Link>
                  <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                    className="text-left text-apple-gray-700 hover:text-apple-blue-500">
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/signup" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="primary" className="w-full">{t('nav.signup')}</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
